/**
 * RaceWorkspace — the focused race-analysis page used for both live and replay.
 * Map first; compact timing tower; driver detail; chronological events; replay
 * controls; Race IQ. Everything reads the provider at one playback cursor.
 */
import React, { Suspense, useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { RaceSessionProvider, useRaceSession } from "./RaceSessionProvider";
import TrackMap from "./components/TrackMap";
import TimingTower from "./components/TimingTower";
import DriverPanel from "./components/DriverPanel";
import EventFeed from "./components/EventFeed";
import ReplayControls from "./components/ReplayControls";
import RaceIQPanel from "./components/RaceIQPanel";
import StatusBar from "./components/StatusBar";
import { InspectorProvider } from "./inspector/InspectorContext";
import { inspectorAllowed } from "./inspector/inspectorApi";
import "./race-workspace.css";

// Developer-only inspector UI (?inspect=1): lazy chunks, never fetched by a normal replay.
const InspectorPanel = React.lazy(() => import("./inspector/InspectorPanel"));
const InspectorHud = React.lazy(() => import("./inspector/InspectorMapOverlay").then((module) => ({ default: module.InspectorHud })));

const STATUS_LABEL = { replay_ready: "Replay", raw_only: "Ingested, replay pending", live: "Live", scheduled: "Scheduled", not_ingested: "Not ingested", subscription_required: "Live (subscription required)", unsupported: "Unsupported", live_pending: "Live pending" };

function SessionPicker() {
    const { catalog, catalogError, sessionKey, mode, loadCatalog } = useRaceSession();
    const navigate = useNavigate();
    const sessions = useMemo(() => {
        const list = catalog?.sessions || [];
        return [...list].filter((item) => item.kind !== "test").sort((a, b) => String(b.date_start || "").localeCompare(String(a.date_start || "")));
    }, [catalog]);
    const ready = sessions.filter((item) => item.capability_status === "replay_ready");
    const live = catalog?.live;
    return (
        <div className="glass flex flex-wrap items-center gap-2 rounded-[24px] border border-white/8 px-3 py-2" data-testid="session-picker">
            <label className="flex min-w-0 flex-1 items-center gap-2">
                <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-600">Session</span>
                <select
                    value={mode === "replay" && sessionKey ? String(sessionKey) : ""}
                    onChange={(event) => {
                        const value = event.target.value;
                        if (value === "live") navigate("/race/live");
                        else if (value) navigate(`/replay/${value}`);
                    }}
                    className="min-w-0 flex-1 rounded-full border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-zinc-100 outline-none transition-colors focus:border-f1-red/45"
                    aria-label="Choose a session"
                >
                    <option value="">{ready.length ? "Choose a replay…" : catalogError ? "Catalog unavailable" : catalog ? "No replays published yet" : "Loading sessions…"}</option>
                    {ready.map((item) => (
                        <option key={item.session_key} value={item.session_key}>
                            {item.year} · {item.circuit_short_name || item.location} · {item.name}
                        </option>
                    ))}
                    {sessions.filter((item) => item.capability_status !== "replay_ready").slice(0, 60).map((item) => (
                        <option key={item.session_key} value="" disabled>
                            {item.year} · {item.circuit_short_name || item.location} · {item.name} — {STATUS_LABEL[item.capability_status] || item.capability_status}
                        </option>
                    ))}
                </select>
            </label>
            <button type="button" onClick={() => navigate("/race/live")} className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 font-mono text-[9px] uppercase tracking-[0.18em] transition-all ${mode === "live" ? "border-f1-red/45 bg-f1-red/18 text-white" : "border-white/10 bg-white/[0.035] text-zinc-300 hover:border-f1-red/35"}`} data-testid="go-live">
                <span className={`h-1.5 w-1.5 rounded-full ${live?.status === "live" ? "bg-f1-red pulse-red" : "bg-zinc-600"}`} />
                {live?.status === "live" ? "Live now" : "Live"}
            </button>
            <button type="button" onClick={loadCatalog} className="rounded-full border border-white/10 bg-white/[0.035] px-3 py-2 font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-500 hover:text-white" title="Refresh the session catalog">
                Refresh
            </button>
        </div>
    );
}

function Keyboard() {
    const session = useRaceSession();
    useEffect(() => {
        const handler = (event) => {
            if (["INPUT", "SELECT", "TEXTAREA"].includes(event.target?.tagName)) return;
            if (event.key === " ") {
                event.preventDefault();
                if (session.mode === "replay") session.togglePlay();
            } else if (event.key === "ArrowRight" && session.mode === "replay") session.stepSeconds(event.shiftKey ? 60 : 10);
            else if (event.key === "ArrowLeft" && session.mode === "replay") session.stepSeconds(event.shiftKey ? -60 : -10);
            else if (event.key.toLowerCase() === "f") session.setFollow(!session.follow);
            else if (event.key === "Escape") session.setSelectedEntry(null);
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [session]);
    return null;
}

function WorkspaceBody({ inspect = false }) {
    const { status, message, mode, snapshot, catalog, sessionKey } = useRaceSession();
    const empty = status === "empty" || (status === "error" && !snapshot);
    return (
        <div className="race-workspace" data-testid="race-workspace" data-mode={mode} data-inspector={inspect ? "on" : undefined}>
            <Keyboard />
            <div className="race-workspace__top">
                <StatusBar />
                <SessionPicker />
            </div>
            {empty ? (
                <div className="race-workspace__empty glass rounded-[28px] border border-white/8 px-6 py-10 text-center">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-600">{mode === "live" ? "Live" : "Replay"}</div>
                    <h2 className="mt-3 font-display text-3xl text-white">{mode === "live" ? "No live session" : "Replay unavailable"}</h2>
                    <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-zinc-500">{message || (mode === "live" ? "The live runner is idle. Recorded replays are available from the session picker." : "No replay has been published for this session.")}</p>
                    {catalog?.live?.next_session && mode === "live" ? <p className="mt-2 text-xs text-zinc-600">Next: {catalog.live.next_session.name} · {String(catalog.live.next_session.date_start).slice(0, 16).replace("T", " ")} UTC</p> : null}
                </div>
            ) : (
                <div className="race-workspace__grid">
                    <aside className="race-workspace__tower glass rounded-[28px] border border-white/8 p-3">
                        <TimingTower className="h-full" />
                    </aside>
                    <main className="race-workspace__main">
                        {inspect && mode === "replay" ? (
                            <Suspense fallback={null}>
                                <InspectorHud />
                            </Suspense>
                        ) : null}
                        <TrackMap className="race-workspace__map" />
                        <ReplayControls className="mt-3" />
                        {inspect && mode === "replay" ? (
                            <Suspense fallback={null}>
                                <InspectorPanel className="mt-3" />
                            </Suspense>
                        ) : null}
                    </main>
                    <aside className="race-workspace__side">
                        <DriverPanel />
                        <div className="glass rounded-[28px] border border-white/8 p-4">
                            <RaceIQPanel className="max-h-[320px]" />
                        </div>
                        <div className="glass rounded-[28px] border border-white/8 p-4">
                            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-600">Events</div>
                            <EventFeed className="max-h-[420px]" />
                        </div>
                    </aside>
                </div>
            )}
            {status === "loading" && !snapshot ? <div className="race-workspace__loading font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">Loading {mode === "live" ? "live state" : `replay ${sessionKey}`}…</div> : null}
        </div>
    );
}

export default function RaceWorkspace({ mode: forcedMode = null }) {
    const params = useParams();
    const location = useLocation();
    const isLive = forcedMode === "live" || location.pathname.endsWith("/live") || location.pathname === "/live-dashboard";
    const sessionKey = !isLive && params.sessionKey ? Number(params.sessionKey) : null;
    // Developer-only engine inspector: /replay/:sessionKey?inspect=1 (development builds or REACT_APP_RACE_INSPECTOR=1).
    const inspect = !isLive && inspectorAllowed(location.search);
    return (
        <RaceSessionProvider key={`${isLive ? "live" : sessionKey || "none"}`} initialSessionKey={sessionKey} initialMode={isLive ? "live" : "replay"} instrument={inspect}>
            <InspectorProvider enabled={inspect}>
                <WorkspaceBody inspect={inspect} />
            </InspectorProvider>
        </RaceSessionProvider>
    );
}
